import { GeoJSON, MapContainer, TileLayer } from "react-leaflet";
import { type LatLngBounds, type Layer, type LeafletMouseEvent, type Path } from "leaflet";
import {
  layerBounds,
  geometryBounds,
  type SubBasinLayer,
  type SubBasinFeature,
} from "../../model/gisLayer.ts";
import type { SubBasin } from "../../model/types.ts";
import { BASEMAPS } from "../basemap.ts";
import { RectangleSelectionLayer, type RectangleSelectionMode } from "../RectangleSelection.tsx";

const UNASSIGNED_STYLE = { color: "#1b4d5c", weight: 1, fillColor: "#cfe9ec", fillOpacity: 0.4 };
const ASSIGNED_STYLE = { color: "#1b4d5c", weight: 1, fillColor: "#3fa7b3", fillOpacity: 0.6 };
const SELECTION_HIGHLIGHT = { color: "#e0913f", weight: 3 };

function isPath(layer: Layer): layer is Path {
  return "setStyle" in layer;
}

/**
 * Sub-basin map with selection, used alongside BulkAssigner in the new
 * project wizard (same role as the map in the original: clicking a
 * sub-basin toggles it, Shift+click adds/Ctrl+click removes it, Shift+drag
 * adds several by rectangle, Ctrl+drag subtracts them — see
 * FormGIS.vb/GIS.vb). The selection is the same state
 * used by the list below, so they stay in sync with each other in both
 * directions.
 *
 * Also colors by whether the sub-basin already has something assigned
 * (`isAssigned`) — an improvement over the original, which had no legend of
 * this kind.
 */
export function AssignmentMap({
  gisLayer,
  project,
  isAssigned,
  selection,
  onChangeSelection,
}: {
  gisLayer: SubBasinLayer;
  project: { subBasins: SubBasin[] };
  isAssigned: (subBasin: SubBasin) => boolean;
  selection: Set<number>;
  onChangeSelection: (selection: Set<number>) => void;
}) {
  const basemap = BASEMAPS[0];
  function subBasinOf(feature: SubBasinFeature): SubBasin | null {
    const idGis = feature.properties.idGis;
    return idGis == null
      ? null
      : (project.subBasins.find((c) => c.gisCode === Number(idGis)) ?? null);
  }

  function styleOf(feature: SubBasinFeature): typeof ASSIGNED_STYLE {
    const subBasin = subBasinOf(feature);
    const base = subBasin && isAssigned(subBasin) ? ASSIGNED_STYLE : UNASSIGNED_STYLE;
    const selected = subBasin != null && selection.has(subBasin.subBasinCode);
    return selected ? { ...base, ...SELECTION_HIGHLIGHT } : base;
  }

  // Plain click toggles; Shift+click adds, Ctrl+click removes — same
  // semantics as Shift+drag/Ctrl+drag below, just for one sub-basin instead
  // of a rectangle's worth (see the equivalent handler in SubBasinMap.tsx).
  function toggle(feature: SubBasinFeature, e: LeafletMouseEvent) {
    const subBasin = subBasinOf(feature);
    if (!subBasin) return;
    const copy = new Set(selection);
    if (e.originalEvent.shiftKey) copy.add(subBasin.subBasinCode);
    else if (e.originalEvent.ctrlKey || e.originalEvent.metaKey) copy.delete(subBasin.subBasinCode);
    else if (copy.has(subBasin.subBasinCode)) copy.delete(subBasin.subBasinCode);
    else copy.add(subBasin.subBasinCode);
    onChangeSelection(copy);
  }

  function selectByRectangle(bounds: LatLngBounds, rectangleMode: RectangleSelectionMode) {
    const copy = new Set(selection);
    for (const f of gisLayer.features) {
      const bbox = geometryBounds(f.geometry);
      if (!bbox) continue;
      const overlaps = bounds.intersects([bbox[0], bbox[1]]);
      if (!overlaps) continue;
      const subBasin = subBasinOf(f);
      if (!subBasin) continue;
      if (rectangleMode === "add") copy.add(subBasin.subBasinCode);
      else copy.delete(subBasin.subBasinCode);
    }
    onChangeSelection(copy);
  }

  return (
    <div className="assignment-map">
      <div className="map small-map">
        <MapContainer bounds={layerBounds(gisLayer)} scrollWheelZoom boxZoom={false}>
          <TileLayer
            attribution={basemap.attribution}
            url={basemap.url}
            maxZoom={basemap.maxZoom}
          />
          <GeoJSON
            key={`${[...selection].join(",")}|${project.subBasins.map((c) => (isAssigned(c) ? 1 : 0)).join("")}`}
            data={gisLayer as unknown as GeoJSON.FeatureCollection}
            style={(feature) => styleOf(feature as unknown as SubBasinFeature)}
            onEachFeature={(feature, layer) => {
              layer.on("click", (e) => toggle(feature as unknown as SubBasinFeature, e));
              layer.on("mouseover", () => isPath(layer) && layer.setStyle({ weight: 2 }));
              layer.on(
                "mouseout",
                () =>
                  isPath(layer) && layer.setStyle(styleOf(feature as unknown as SubBasinFeature))
              );
            }}
          />
          <RectangleSelectionLayer onSelect={selectByRectangle} />
        </MapContainer>
      </div>
    </div>
  );
}
